/**
 * @file Procedural audio (GDD §14).
 *
 * Every sound is synthesised with the Web Audio API — no files, no library, no
 * loading time. Voices are built per shot from oscillators and a shared noise
 * buffer, which is cheap enough for the fire rates this game reaches and keeps
 * the asset folder empty while the game has no art direction.
 *
 * The manager only listens to gameplay events; nothing in the game calls it.
 */

import { Events } from '../core/EventBus.js';
import { PlayerControlled } from '../components/index.js';
import { randomRange, clamp } from '../utils/MathUtils.js';
import { loadNumber, saveNumber } from '../utils/Persisted.js';

import reloadLoopUrl from './reload/reload_1st.webm';
import reloadRocketUrl from './reload/reload_rocket.webm';
import reloadEndUrl from './reload/reload_end.webm';
import emptyShotUrl from './reload/emptyshot.webm';
import dashUrl from './tp.webm';

/** `localStorage` key the SFX volume slider persists under. */
const VOLUME_STORAGE_KEY = 'spin:sfxVolume';

/**
 * Attenuation for `emptyshot.webm`. The source file decodes with a peak
 * sample of ~1.28 — already clipping above 0 dBFS — against ~0.5-0.7 for the
 * other recorded samples, so played at its raw level it reads as a harsh,
 * distorted spike rather than a click; held on an empty auto weapon (the
 * shotgun repeats this roughly every 0.4s) that spike landing over and over
 * is what actually produces the "way too loud" complaint, not stacked
 * voices — `Weapon.tryFire` already gates one dry-fire attempt per cadence
 * tick, confirmed by counting live `WEAPON_EMPTY_TRIGGER` events. Brings the
 * peak down to ~0.58, in line with `reload_end`'s.
 */
const EMPTY_SHOT_GAIN = 0.45;

/**
 * Shot voices per weapon key. A weapon with no entry uses `default`, so adding
 * a weapon never requires touching the audio manager — only re-tuning one does.
 *
 * @type {Readonly<Record<string, object>>}
 */
const SHOT_PROFILES = Object.freeze({
  default: {
    peak: 0.5,
    decay: 0.1,
    frequency: [320, 400],
    drop: 70,
    dropTime: 0.09,
    stop: 0.12,
    noise: { duration: 0.05, frequency: 1800, peak: 0.28, type: 'highpass' }
  },
  /** Deep, slow, wide: the blast has to sound like weight, not like a faster pistol. */
  shotgun: {
    peak: 0.62,
    decay: 0.22,
    frequency: [200, 260],
    drop: 42,
    dropTime: 0.18,
    stop: 0.26,
    noise: { duration: 0.16, frequency: 900, peak: 0.42, type: 'bandpass' }
  }
});

/**
 * Per-round reload voices keyed by `reloadPresentation.style` (issue #78). Each
 * bala/shell/cell/canister that seats during a reload plays one of these; the
 * style is what makes a pistol magazine tick differently from a shotgun shell or
 * a flamethrower canister without the audio manager knowing any weapon key.
 *
 * `rise` bends the pitch up as the magazine fills so a run of ticks has contour;
 * `jitter` breaks up identical repeats.
 *
 * @type {Readonly<Record<string, object>>}
 */
const RELOAD_ROUND_PROFILES = Object.freeze({
  /** Light metallic click: a round pressed into a magazine. */
  magazine: {
    tone: 'square',
    frequency: 440,
    drop: 210,
    dropTime: 0.03,
    stop: 0.05,
    peak: 0.16,
    decay: 0.05,
    rise: 0.45,
    jitter: 0.06,
    noise: { duration: 0.03, frequency: 3000, peak: 0.12, type: 'highpass' }
  },
  /** Woody thock of a shell thumbed into a tube. */
  single_round: {
    tone: 'triangle',
    frequency: 300,
    drop: 150,
    dropTime: 0.04,
    stop: 0.07,
    peak: 0.22,
    decay: 0.07,
    rise: 0.12,
    jitter: 0.05,
    noise: { duration: 0.045, frequency: 1500, peak: 0.2, type: 'bandpass' }
  },
  /** Heavy, low clunk: a missile dropped into the tube. Never a small click. */
  heavy_round: {
    tone: 'sine',
    frequency: 150,
    drop: 70,
    dropTime: 0.07,
    stop: 0.12,
    peak: 0.3,
    decay: 0.12,
    rise: 0.05,
    jitter: 0.04,
    noise: { duration: 0.07, frequency: 700, peak: 0.24, type: 'lowpass' }
  },
  /** Pressurised hiss/chuff of a fuel canister seating. */
  canister: {
    tone: 'sawtooth',
    frequency: 120,
    drop: 80,
    dropTime: 0.05,
    stop: 0.08,
    peak: 0.12,
    decay: 0.06,
    rise: 0.02,
    jitter: 0.08,
    noise: { duration: 0.05, frequency: 1200, peak: 0.16, type: 'bandpass' }
  }
});

export class AudioManager {
  /**
   * @param {import('../core/EventBus.js').EventBus} events Shared event bus.
   * @param {import('../config/GameConfig.js').GameConfig} config Game config.
   */
  constructor(events, config) {
    /** @type {import('../core/EventBus.js').EventBus} @private */
    this._events = events;
    /** @type {import('../config/GameConfig.js').GameConfig} @private */
    this._config = config;
    /** @type {AudioContext | null} @private */
    this._context = null;
    /** @type {GainNode | null} @private */
    this._master = null;
    /** @type {AudioBuffer | null} @private */
    this._noise = null;
    /** @type {number} Timestamp of the last shot, used to throttle. @private */
    this._lastShot = -Infinity;
    /**
     * Timestamp of the last reload tick. High-capacity weapons (the 80-round
     * flamethrower) present rounds fast enough to pile voices onto the master;
     * this floor is well under any real tick cadence, so it protects the mix
     * without ever dropping a tick a player could actually hear as distinct.
     * @type {number}
     * @private
     */
    this._lastReloadTick = -Infinity;
    /**
     * Own copy of the volume rather than a live read of `config.audio`: the
     * shared `CONFIG` singleton must stay untouched so two `Game` instances
     * never leak a volume change into one another (see Context.js).
     * @type {number}
     * @private
     */
    this._volume = clamp(loadNumber(VOLUME_STORAGE_KEY, config.audio.masterVolume), 0, 1);
    /** @type {(() => void)[]} @private */
    this._subscriptions = [];
    /** @type {boolean} Whether the flamethrower noise loop is running. @private */
    this._flamePlaying = false;
    /** @type {AudioBufferSourceNode | null} @private */
    this._flameSource = null;
    /** @type {GainNode | null} @private */
    this._flameGain = null;
    /** @type {BiquadFilterNode | null} @private */
    this._flameFilter = null;

    /**
     * Recorded voices: reload loop, reload end clack, dry-fire click. Kept
     * apart from the procedural cues elsewhere in this file — those still fire
     * for every carrier (per-round ticks, the enemy-audible synth clack), these
     * are player only, the way the player's shots already do not borrow an
     * enemy's weapon voice.
     * @type {Map<string, AudioBuffer>}
     * @private
     */
    this._sampleBuffers = new Map();
    /** @type {Promise<void> | null} @private */
    this._samplesLoading = null;
    /** @type {boolean} Whether the reload loop is currently running. @private */
    this._reloadVoicePlaying = false;
    /** @type {AudioBufferSourceNode | null} @private */
    this._reloadVoiceSource = null;
    /** @type {GainNode | null} @private */
    this._reloadVoiceGain = null;

    this._subscribe();
    this._installUnlockHandler();
  }

  /**
   * Browsers refuse to start an AudioContext before a user gesture, so the
   * context is created lazily on the first interaction.
   * @returns {void}
   * @private
   */
  _installUnlockHandler() {
    const unlock = () => {
      this.resume();
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);
  }

  /** @returns {void} @private */
  _subscribe() {
    this._subscriptions.push(
      this._events.on(Events.WEAPON_FIRED, (payload) => {
        // A stream ticks tryFire ~24 times a second; its voice is the flame
        // loop below, never two dozen blips.
        if (payload?.weapon?.streaming) return;
        // Heavy weapons get their own voice: the launcher's whoosh and the
        // rail's crack read wrong through the generic blip.
        if (payload?.weapon?.key === 'rocket_launcher') this.playRocketLaunch();
        else if (payload?.weapon?.key === 'railgun') this.playRailShot();
        else this.playShot(payload?.weapon?.key);
      }),
      // Fires on the shot that empties the magazine — informational, not a
      // failed action, so this is the procedural cue, not the recorded one.
      this._events.on(Events.WEAPON_EMPTY, () => this.playEmptyClick()),
      // Fires on an actual dry trigger pull (currentAmmo already 0 when the
      // shot was attempted) — this is "firing with no bullets", the recorded
      // sample, player only for the same reason the reload voices are.
      this._events.on(Events.WEAPON_EMPTY_TRIGGER, (payload) => {
        if (isPlayer(payload?.owner)) this.playEmptyShot();
      }),
      // One short tick per bala/shell/cell/canister seated during a reload.
      this._events.on(Events.RELOAD_ROUND_LOADED, (payload) =>
        this.playReloadRound(payload?.roundIndex, payload?.roundsTotal, payload?.style)
      ),
      // Recorded reload voices, player only: an enemy Spitter reloads through
      // the same component but should stay silent, the way its shots already
      // do not borrow the player's weapon voices. Started/stopped independent
      // of the procedural cues below — the loop tracks the whole reload
      // window, the end sample only ever plays on a genuine completion.
      this._events.on(Events.RELOAD_STARTED, (payload) => {
        if (isPlayer(payload?.owner)) this.startReloadVoice(payload?.weapon?.key);
      }),
      // Final "seated" clack: the magazine/bolt/canister closing.
      this._events.on(Events.RELOAD_COMPLETED, (payload) => {
        this.playReloadDone();
        if (isPlayer(payload?.owner)) {
          this.stopReloadVoice();
          this.playReloadEnd();
        }
      }),
      // Cancelled (weapon swapped mid-reload) and interrupted (fire cut a
      // reload short) both end the reload without a real completion: the loop
      // stops but, same as the procedural clack above, the end sample does not
      // play.
      this._events.on(Events.RELOAD_CANCELLED, (payload) => {
        if (isPlayer(payload?.owner)) this.stopReloadVoice();
      }),
      this._events.on(Events.RELOAD_INTERRUPTED, (payload) => {
        if (isPlayer(payload?.owner)) this.stopReloadVoice();
      }),
      // DashSystem only ever queries PlayerControlled entities, but the guard
      // is kept for consistency with the other recorded, player-only cues.
      this._events.on(Events.PLAYER_DASHED, (payload) => {
        if (isPlayer(payload?.entity)) this.playDash();
      }),
      this._events.on(Events.BULLET_HIT, () => this.playImpact()),
      this._events.on(Events.DIED, () => this.playDeath()),
      this._events.on(Events.EXPLOSION, () => this.playExplosion()),
      this._events.on(Events.FOOTSTEP, () => this.playFootstep()),
      // Streaming weapons use a sustained loop, not one-shot blips.
      this._events.on(Events.FLAME_START, () => this.playFlameStart()),
      this._events.on(Events.FLAME_END, () => this.playFlameEnd()),
      // Any state change (pause, death, menu) silences loops that would
      // otherwise keep running under a screen where nothing is happening.
      this._events.on(Events.STATE_CHANGED, () => {
        this.playFlameEnd();
        this.stopReloadVoice();
      }),
      this._events.on(Events.WEAPON_THROWN, () => this.playThrow())
    );
  }

  /**
   * Creates the audio graph if needed and resumes a suspended context.
   * @returns {void}
   */
  resume() {
    if (!this._context) this._create();
    if (this._context.state === 'suspended') this._context.resume();
  }

  /** @returns {void} @private */
  _create() {
    const Ctor = window.AudioContext ?? /** @type {any} */ (window).webkitAudioContext;
    if (!Ctor) return;

    this._context = new Ctor();
    this._master = this._context.createGain();
    this._master.gain.value = this._volume;
    this._master.connect(this._context.destination);
    this._noise = this._createNoiseBuffer(0.4);
    this._loadRecordedSounds();
  }

  /**
   * Fetches and decodes the two recorded reload samples. Kicked off once the
   * context exists (`decodeAudioData` needs one) rather than at construction,
   * and safe to call more than once — every caller shares the one promise.
   * @returns {Promise<void>}
   * @private
   */
  _loadRecordedSounds() {
    if (this._samplesLoading) return this._samplesLoading;

    this._samplesLoading = Promise.all(
      [
        ['loop', reloadLoopUrl],
        ['rocket', reloadRocketUrl],
        ['end', reloadEndUrl],
        ['emptyShot', emptyShotUrl],
        ['dash', dashUrl]
      ].map(async ([key, url]) => {
        const response = await fetch(url);
        const bytes = await response.arrayBuffer();
        const buffer = await this._context.decodeAudioData(bytes);
        this._sampleBuffers.set(key, buffer);
      })
    ).then(() => undefined);

    return this._samplesLoading;
  }

  /**
   * @returns {number} Current SFX volume, 0..1.
   */
  getVolume() {
    return this._volume;
  }

  /**
   * Sets the SFX volume, applied immediately and persisted for next launch.
   * @param {number} value New volume, 0..1 (out-of-range values are clamped).
   * @returns {void}
   */
  setVolume(value) {
    this._volume = clamp(value, 0, 1);
    if (this._master) this._master.gain.value = this._volume;
    saveNumber(VOLUME_STORAGE_KEY, this._volume);
  }

  /**
   * @param {number} seconds Buffer length.
   * @returns {AudioBuffer} White noise, reused by every noise-based voice.
   * @private
   */
  _createNoiseBuffer(seconds) {
    const length = Math.floor(this._context.sampleRate * seconds);
    const buffer = this._context.createBuffer(1, length, this._context.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
    return buffer;
  }

  /**
   * Pauses or resumes all output, used by the pause state.
   * @param {boolean} suspended Whether audio should be silent.
   * @returns {void}
   */
  setSuspended(suspended) {
    if (!this._context) return;
    if (suspended) this._context.suspend();
    else this._context.resume();
  }

  /** @returns {boolean} True when the graph is ready to play. @private */
  get _ready() {
    return Boolean(this._context && this._master && this._context.state === 'running');
  }

  /**
   * Builds a gain node with a percussive envelope.
   * @param {number} peak Peak gain.
   * @param {number} attack Attack time in seconds.
   * @param {number} decay Decay time in seconds.
   * @returns {GainNode}
   * @private
   */
  _envelope(peak, attack, decay) {
    const now = this._context.currentTime;
    const gain = this._context.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(peak, now + attack);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + attack + decay);
    gain.connect(this._master);
    return gain;
  }

  /**
   * Punchy layered blip: a falling square for body, noise for the crack.
   * @param {string} [weaponKey] Archetype key, to pick a voice; unknown keys
   *   fall back to the standard one.
   * @returns {void}
   */
  playShot(weaponKey) {
    if (!this._ready) return;
    const now = this._context.currentTime;
    // Throttle: at 8 shots/second overlapping voices would clip the master.
    if (now - this._lastShot < this._config.audio.minInterval) return;
    this._lastShot = now;

    const profile = SHOT_PROFILES[weaponKey] ?? SHOT_PROFILES.default;

    const gain = this._envelope(profile.peak, 0.004, profile.decay);
    const oscillator = this._context.createOscillator();
    oscillator.type = 'square';
    const [low, high] = profile.frequency;
    oscillator.frequency.setValueAtTime(randomRange(low, high), now);
    oscillator.frequency.exponentialRampToValueAtTime(profile.drop, now + profile.dropTime);
    oscillator.connect(gain);
    oscillator.start(now);
    oscillator.stop(now + profile.stop);

    const { duration, frequency, peak, type } = profile.noise;
    this._playNoise(duration, frequency, peak, type);
  }

  /**
   * Dry mechanical click for a trigger pull on an empty magazine.
   * @returns {void}
   */
  playEmptyClick() {
    if (!this._ready) return;
    this._playNoise(0.035, 3400, 0.18, 'bandpass');
  }

  /**
   * The Lance: a dry, energetic crack. A sawtooth snapping from 800 Hz down to
   * 200 Hz in 50 ms gives the tone, a tight band of noise gives the whip-crack.
   * @returns {void}
   */
  playRailShot() {
    if (!this._ready) return;
    const now = this._context.currentTime;
    this._lastShot = now;

    const gain = this._envelope(0.45, 0.003, 0.07);
    const oscillator = this._context.createOscillator();
    oscillator.type = 'sawtooth';
    oscillator.frequency.setValueAtTime(randomRange(760, 840), now);
    oscillator.frequency.exponentialRampToValueAtTime(200, now + 0.05);
    oscillator.connect(gain);
    oscillator.start(now);
    oscillator.stop(now + 0.09);

    this._playNoise(0.08, 4000, 0.3, 'bandpass');
  }

  /**
   * Dead trigger over an empty magazine: mechanism, no tone.
   * @returns {void}
   */
  playEmptyClick() {
    if (!this._ready) return;
    this._playNoise(0.03, 3200, 0.18, 'highpass');
  }

  /**
   * One round seated during a reload (issue #78). The voice is picked by the
   * weapon's presentation style, and the pitch bends up as the magazine fills so
   * a run of six or twelve ticks reads as a sequence, not a stutter of identical
   * clicks.
   *
   * @param {number} [roundIndex] 1-based index of the round just seated.
   * @param {number} [roundsTotal] Rounds this reload seats in total.
   * @param {string} [style] `reloadPresentation.style`; unknown styles fall back
   *   to the magazine voice.
   * @returns {void}
   */
  playReloadRound(roundIndex = 1, roundsTotal = 1, style = 'magazine') {
    if (!this._ready) return;
    const now = this._context.currentTime;
    if (now - this._lastReloadTick < 0.012) return;
    this._lastReloadTick = now;

    const profile = RELOAD_ROUND_PROFILES[style] ?? RELOAD_ROUND_PROFILES.magazine;
    const progress = roundsTotal > 1 ? (roundIndex - 1) / (roundsTotal - 1) : 0;
    const bend = 1 + progress * profile.rise + (Math.random() - 0.5) * profile.jitter;

    const gain = this._envelope(profile.peak, 0.003, profile.decay);
    const oscillator = this._context.createOscillator();
    oscillator.type = profile.tone;
    oscillator.frequency.setValueAtTime(profile.frequency * bend, now);
    oscillator.frequency.exponentialRampToValueAtTime(
      Math.max(1, profile.drop * bend),
      now + profile.dropTime
    );
    oscillator.connect(gain);
    oscillator.start(now);
    oscillator.stop(now + profile.stop);

    const { duration, frequency, peak, type } = profile.noise;
    this._playNoise(duration, frequency * bend, peak, type);
  }

  /**
   * Magazine seated. Two quick ticks so the reload ends with a cue rather than
   * with silence.
   * @returns {void}
   */
  playReloadDone() {
    if (!this._ready) return;
    const now = this._context.currentTime;

    const gain = this._envelope(0.22, 0.004, 0.06);
    const oscillator = this._context.createOscillator();
    oscillator.type = 'triangle';
    oscillator.frequency.setValueAtTime(520, now);
    oscillator.frequency.exponentialRampToValueAtTime(880, now + 0.05);
    oscillator.connect(gain);
    oscillator.start(now);
    oscillator.stop(now + 0.08);

    this._playNoise(0.04, 2400, 0.14, 'bandpass');
  }

  /**
   * Starts the recorded reload voice for the duration of a player reload,
   * underneath the procedural per-round ticks. Idempotent, like
   * {@link AudioManager#playFlameStart}: a stray second call while already
   * playing is a no-op rather than a stacked voice.
   *
   * Most weapons loop `reload_1st` for the whole window. The Rocket Launcher
   * is the one exception: a missile seats once, so `reload_rocket` plays a
   * single pass instead of repeating underneath a reload that does not
   * actually repeat any action.
   *
   * @param {string} [weaponKey] Archetype key from the reload payload.
   * @returns {void}
   */
  startReloadVoice(weaponKey) {
    if (this._reloadVoicePlaying) return;
    this._reloadVoicePlaying = true;
    this.resume();
    this._loadRecordedSounds().then(() => this._startReloadVoiceSource(weaponKey));
  }

  /**
   * Actually starts the voice source, once the buffer is decoded. Split from
   * {@link AudioManager#startReloadVoice} because decoding is async and the
   * reload it was meant for may already be over by the time it resolves.
   * @param {string} [weaponKey] Archetype key from the reload payload.
   * @returns {void}
   * @private
   */
  _startReloadVoiceSource(weaponKey) {
    if (!this._reloadVoicePlaying || !this._ready) return;
    const isRocket = weaponKey === 'rocket_launcher';
    const buffer = this._sampleBuffers.get(isRocket ? 'rocket' : 'loop');
    if (!buffer) return;

    const now = this._context.currentTime;
    const gain = this._context.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(1, now + 0.03);
    gain.connect(this._master);

    const source = this._context.createBufferSource();
    source.buffer = buffer;
    source.loop = !isRocket;
    source.connect(gain);
    source.start(now);

    this._reloadVoiceSource = source;
    this._reloadVoiceGain = gain;
  }

  /**
   * Stops the reload voice, if one is running — looping or the Rocket
   * Launcher's single pass alike. Safe to call at any time — pause, a weapon
   * swap, death and a genuine reload completion all funnel through here, same
   * as {@link AudioManager#playFlameEnd} for the flamethrower's loop.
   * @returns {void}
   */
  stopReloadVoice() {
    if (!this._reloadVoicePlaying) return;
    this._reloadVoicePlaying = false;

    const source = this._reloadVoiceSource;
    const gain = this._reloadVoiceGain;
    this._reloadVoiceSource = null;
    this._reloadVoiceGain = null;
    if (!source || !gain || !this._context) return;

    // Ramp out before stopping, so playback never cuts mid-waveform.
    const now = this._context.currentTime;
    try {
      gain.gain.cancelScheduledValues(now);
      gain.gain.setValueAtTime(gain.gain.value, now);
      gain.gain.linearRampToValueAtTime(0.0001, now + 0.04);
      source.stop(now + 0.05);
    } catch {
      // Context already closed or source already stopped.
    }
    source.onended = () => {
      try {
        source.disconnect();
        gain.disconnect();
      } catch {
        // Graph already torn down.
      }
    };
  }

  /**
   * One-shot recorded clack for a genuine reload completion — distinct from
   * the procedural {@link AudioManager#playReloadDone} blip, which still
   * plays for every carrier so a reloading enemy is not silent.
   * @returns {void}
   */
  playReloadEnd() {
    this.resume();
    this._loadRecordedSounds().then(() => {
      if (!this._ready) return;
      const buffer = this._sampleBuffers.get('end');
      if (!buffer) return;

      const source = this._context.createBufferSource();
      source.buffer = buffer;
      source.connect(this._master);
      source.start(this._context.currentTime);
    });
  }

  /**
   * One-shot recorded dry-fire click: the trigger was pulled with nothing in
   * the chamber. Player only — {@link Events.WEAPON_EMPTY_TRIGGER} is already
   * cadence-limited at the source (`Weapon.tryFire` gates it on the weapon's
   * own cooldown), so no extra throttling is needed here. Routed through
   * {@link EMPTY_SHOT_GAIN} rather than straight to `_master`, unlike the
   * other one-shot samples — see that constant for why.
   * @returns {void}
   */
  playEmptyShot() {
    this.resume();
    this._loadRecordedSounds().then(() => {
      if (!this._ready) return;
      const buffer = this._sampleBuffers.get('emptyShot');
      if (!buffer) return;

      const gain = this._context.createGain();
      gain.gain.value = EMPTY_SHOT_GAIN;
      gain.connect(this._master);

      const source = this._context.createBufferSource();
      source.buffer = buffer;
      source.connect(gain);
      source.start(this._context.currentTime);
    });
  }

  /**
   * One-shot recorded cue for the player's dash (`tp.webm`). Fires once per
   * dash — `DashSystem` only calls this on the frame a dash actually starts,
   * never per tick of its duration.
   * @returns {void}
   */
  playDash() {
    this.resume();
    this._loadRecordedSounds().then(() => {
      if (!this._ready) return;
      const buffer = this._sampleBuffers.get('dash');
      if (!buffer) return;

      const source = this._context.createBufferSource();
      source.buffer = buffer;
      source.connect(this._master);
      source.start(this._context.currentTime);
    });
  }

  /**
   * Rocket launch: a low sustained whoosh instead of a crack, so the launch and
   * the detonation that follows a second later never blur into one sound.
   * @returns {void}
   */
  playRocketLaunch() {
    if (!this._ready) return;
    const now = this._context.currentTime;

    const gain = this._envelope(0.42, 0.03, 0.34);
    const oscillator = this._context.createOscillator();
    oscillator.type = 'sawtooth';
    oscillator.frequency.setValueAtTime(randomRange(150, 190), now);
    oscillator.frequency.exponentialRampToValueAtTime(58, now + 0.32);
    oscillator.connect(gain);
    oscillator.start(now);
    oscillator.stop(now + 0.38);

    // Escaping propellant, wide and soft.
    this._playNoise(0.3, 900, 0.24, 'lowpass');
  }

  /**
   * Detonation: a body of sub-bass under a broad noise crack.
   * @returns {void}
   */
  playExplosion() {
    if (!this._ready) return;
    const now = this._context.currentTime;

    const gain = this._envelope(0.6, 0.006, 0.5);
    const oscillator = this._context.createOscillator();
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(randomRange(110, 150), now);
    oscillator.frequency.exponentialRampToValueAtTime(28, now + 0.45);
    oscillator.connect(gain);
    oscillator.start(now);
    oscillator.stop(now + 0.55);

    this._playNoise(0.42, 420, 0.4, 'lowpass');
  }

  /**
   * Dry click of a trigger pulled on an empty magazine.
   * @returns {void}
   */
  playEmptyClick() {
    if (!this._ready) return;
    this._playNoise(0.04, 3200, 0.16, 'highpass');
  }

  /**
   * Short bright tick.
   * @returns {void}
   */
  playImpact() {
    if (!this._ready) return;
    this._playNoise(0.07, 2600, 0.22, 'bandpass');
  }

  /**
   * Dry whoosh: air moving, no muzzle crack, so a throw never reads as a shot.
   * @returns {void}
   */
  playThrow() {
    if (!this._ready) return;
    this._playNoise(0.18, 700, 0.3, 'bandpass');
  }

  /**
   * Dry click of a trigger pulled on an empty magazine: no body, just the
   * mechanism, so it can never be mistaken for a shot.
   * @returns {void}
   */
  playEmptyClick() {
    if (!this._ready) return;
    this._playNoise(0.03, 3200, 0.18, 'highpass');
  }

  /**
   * Two-part clack: magazine out, magazine in.
   * @returns {void}
   */
  playReload() {
    if (!this._ready) return;
    const now = this._context.currentTime;

    const gain = this._envelope(0.24, 0.004, 0.06);
    const oscillator = this._context.createOscillator();
    oscillator.type = 'square';
    oscillator.frequency.setValueAtTime(180, now);
    oscillator.frequency.exponentialRampToValueAtTime(90, now + 0.05);
    oscillator.connect(gain);
    oscillator.start(now);
    oscillator.stop(now + 0.08);

    this._playNoise(0.05, 1400, 0.2, 'bandpass');
  }

  /**
   * Falling saw: reads as "something broke".
   * @returns {void}
   */
  playDeath() {
    if (!this._ready) return;
    const now = this._context.currentTime;

    const gain = this._envelope(0.36, 0.01, 0.34);
    const oscillator = this._context.createOscillator();
    oscillator.type = 'sawtooth';
    oscillator.frequency.setValueAtTime(randomRange(220, 300), now);
    oscillator.frequency.exponentialRampToValueAtTime(45, now + 0.3);
    oscillator.connect(gain);
    oscillator.start(now);
    oscillator.stop(now + 0.36);
  }

  /**
   * Low muffled thud.
   * @returns {void}
   */
  playFootstep() {
    if (!this._ready) return;
    const now = this._context.currentTime;

    const gain = this._envelope(0.16, 0.006, 0.07);
    const oscillator = this._context.createOscillator();
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(randomRange(90, 130), now);
    oscillator.frequency.exponentialRampToValueAtTime(50, now + 0.07);
    oscillator.connect(gain);
    oscillator.start(now);
    oscillator.stop(now + 0.09);
  }

  /**
   * Starts a filtered noise loop for the flamethrower (issue #14). Idempotent:
   * a second start while already playing is a no-op so mid-stream weapon swaps
   * cannot stack loops.
   * @returns {void}
   */
  playFlameStart() {
    if (this._flamePlaying) return;
    this.resume();
    if (!this._ready) return;

    // Longer than the shared impact noise so a seamless loop does not click.
    const buffer = this._createNoiseBuffer(2);

    const now = this._context.currentTime;
    const gain = this._context.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.35, now + 0.05);
    gain.connect(this._master);

    const filter = this._context.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 800;
    filter.Q.value = 0.7;
    filter.connect(gain);

    const source = this._context.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    source.connect(filter);
    source.start(now);

    this._flameSource = source;
    this._flameGain = gain;
    this._flameFilter = filter;
    this._flamePlaying = true;
  }

  /**
   * Stops the flamethrower loop immediately. Safe to call when nothing is
   * playing — pause, death and weapon swap all funnel through here.
   * @returns {void}
   */
  playFlameEnd() {
    if (!this._flamePlaying) return;
    this._flamePlaying = false;

    try {
      this._flameSource?.stop();
    } catch {
      // Already stopped.
    }
    try {
      this._flameSource?.disconnect();
      this._flameFilter?.disconnect();
      this._flameGain?.disconnect();
    } catch {
      // Graph already torn down.
    }

    this._flameSource = null;
    this._flameFilter = null;
    this._flameGain = null;
  }

  /**
   * Plays a filtered slice of the shared noise buffer.
   * @param {number} duration Length in seconds.
   * @param {number} frequency Filter centre/cutoff in Hz.
   * @param {number} peak Peak gain.
   * @param {BiquadFilterType} type Filter type.
   * @returns {void}
   * @private
   */
  _playNoise(duration, frequency, peak, type) {
    const now = this._context.currentTime;

    const gain = this._envelope(peak, 0.003, duration);
    const filter = this._context.createBiquadFilter();
    filter.type = type;
    filter.frequency.value = frequency;
    filter.Q.value = 1.2;
    filter.connect(gain);

    const source = this._context.createBufferSource();
    source.buffer = this._noise;
    // Random offset so repeated hits never sound identical.
    const offset = Math.random() * (this._noise.duration - duration);
    source.connect(filter);
    source.start(now, Math.max(0, offset), duration + 0.02);
  }

  /**
   * Unsubscribes and closes the audio context.
   * @returns {void}
   */
  dispose() {
    this.playFlameEnd();
    this.stopReloadVoice();
    for (const unsubscribe of this._subscriptions) unsubscribe();
    this._subscriptions.length = 0;
    this._context?.close();
    this._context = null;
    this._master = null;
    this._noise = null;
    this._sampleBuffers.clear();
    this._samplesLoading = null;
  }
}

/**
 * @param {import('../ecs/Entity.js').Entity | null | undefined} entity Reload
 *   event's `owner`.
 * @returns {boolean} Whether the reload belongs to the player, not an enemy
 *   carrying the same weapon component (e.g. the Spitter's Machinegun).
 */
function isPlayer(entity) {
  return Boolean(entity?.alive && entity.has(PlayerControlled));
}
